import React, { createContext, useContext, FunctionComponent, ReactNode, useState } from 'react';

import { PeopleManagementFilterType } from './PeopleManagementFilter';

export interface PeopleManagementState {
  search: string;
  filter: PeopleManagementFilterType;
  sort: string;
  page: number;
}

interface PeopleManagementContextType {
  state: PeopleManagementState;
  updateSearch: (search: string) => void;
  updateFilter: (filter: PeopleManagementFilterType) => void;
  updateSort: (sort: string) => void;
  updatePage: (page: number) => void;
}

const PeopleManagementContext = createContext<PeopleManagementContextType | undefined>(undefined);

interface PeopleManagementProviderProps {
  children: ReactNode;
}

export const PeopleManagementProvider: FunctionComponent<PeopleManagementProviderProps> = ({
  children,
}) => {
  const [state, setState] = useState<PeopleManagementState>({
    search: '',
    filter: 'all',
    sort: 'default',
    page: 1,
  });

  const updateSearch = (search: string): void => {
    setState((prev) => ({ ...prev, search, page: 1 })); // Reset to page 1 when searching
  };

  const updateFilter = (filter: PeopleManagementFilterType): void => {
    setState((prev) => ({ ...prev, filter, page: 1 })); // Reset to page 1 when filtering
  };

  const updateSort = (sort: string): void => {
    setState((prev) => ({ ...prev, sort, page: 1 })); // Reset to page 1 when sorting
  };

  const updatePage = (page: number): void => {
    setState((prev) => ({ ...prev, page }));
  };

  const contextValue: PeopleManagementContextType = {
    state,
    updateSearch,
    updateFilter,
    updateSort,
    updatePage,
  };

  return (
    <PeopleManagementContext.Provider value={contextValue}>
      {children}
    </PeopleManagementContext.Provider>
  );
};

export const usePeopleManagementContext = (): PeopleManagementContextType => {
  const context = useContext(PeopleManagementContext);
  if (!context) {
    throw new Error('usePeopleManagementContext must be used within a PeopleManagementProvider');
  }
  return context;
};
